import { inject, Injector } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { catchError, map, of, switchMap } from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../../common/constants/common/validation-error-codes';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { DataProvidersService } from '../../../../../common/services/data-providers.service';
import type { ContractorFormResolverData } from '../../contractors.routes';
import { ContractorsRoute } from '../enums/contractors-route';
import { ContractorFormHelper } from '../helpers/contractor-form.helper';
import { ContractorHelper } from '../helpers/contractor.helper';
import type { Contractor } from '../interfaces/contractor';
import { ContractorsService } from '../services/contractors.service';

export const editContractorResolver: ResolveFn<
  ContractorFormResolverData & {
    contractor: Contractor;
  }
> = (route: ActivatedRouteSnapshot) => {
  const contractorsService = inject(ContractorsService);
  const dataProvidersService = inject(DataProvidersService);
  const toastService = inject(OnyxToastService);
  const injector = inject(Injector);
  const router = inject(Router);

  const uuid = route.paramMap.get('uuid')!;
  const urlTree = router.parseUrl(ContractorsRoute.CONTRACTORS_LIST);

  return contractorsService.getContractor(uuid).pipe(
    switchMap((contractor) => {
      if (ContractorHelper.isArchived(contractor)) {
        toastService.showError({
          message: 'toasts.archived',
          keepOnNavigation: true,
        });
        return of(new RedirectCommand(urlTree, { replaceUrl: true }));
      }

      const form = ContractorFormHelper.fromDto(contractor);
      const { country, vatId } = form.companyProfile;

      return dataProvidersService.getCompanyData(country, vatId).pipe(
        catchError(() => of(null)),
        map((companyData) => {
          if (companyData) {
            form.companyProfile.companyName = companyData.companyName;
            form.addresses.mainAddress = companyData.mainAddress;
          }

          return {
            form,
            initial: form,
            companyData,
            contractor,
          };
        }),
      );
    }),
    catchError((response: OnyxHttpErrorResponse) => {
      if (VALIDATION_ERROR_CODES.includes(response.status)) {
        toastService.showError({
          message: 'contractors.toasts.contractorNotFound',
          keepOnNavigation: true,
        });
        response.handled = true;
      } else {
        ValidationHelper.handleUnexpectedResponse(response, injector, {
          keepOnNavigation: true,
        });
      }

      return of(new RedirectCommand(urlTree, { replaceUrl: true }));
    }),
  );
};
